import React, {
  useState,
  useCallback,
  useEffect
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  FormControl,
  Autocomplete,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  Tooltip,
  Chip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Alert from '@mui/material/Alert';
import FormFooter from './FormFooter';
import usePersonnelToVerify from '../hooks/usePersonnelToVerify';
import useSessionInfo from '../hooks/useSessionInfo';
import useUpdateSessionInfo from '../hooks/updateSessionInfo';
import usePersonnelToVerifyFields from '../hooks/usePersonnelToVerifyFields';
import useUpdateSelectedFields from '../hooks/useUpdateSelectedFields';

const UpdateRecords = () => {
  const { id: sessionId } = useParams();

  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const { data: viewDifferences } = useSessionInfo(sessionId);
  const [viewDifferencesOnly, setViewDifferencesOnly] = useState(viewDifferences);
  const [displayUpdateButton, setDisplayUpdateButton] = useState(false);
  const { data: options } = usePersonnelToVerify(sessionId);
  const [searchParams, setSearchParams] = useSearchParams();
  const mappedId = searchParams.get('mappedId');
  const [selectedFields, setSelectedFields] = useState([]);

  const setMappedId = (id) => setSearchParams((state) => {
    state.set('mappedId', id);

    return state;
  });

  useEffect(() => {
    const value = Number(mappedId ?? 0);
    if (options?.length > 0) {
      setSelectedPersonnel(options.find((option) => option.id === value));
    }
  }, [options, mappedId]);

  const fieldData = usePersonnelToVerifyFields(sessionId, mappedId);
  const updateSessionInfo = useUpdateSessionInfo();
  const updateMutation = useUpdateSelectedFields(setSelectedPersonnel);

  const filteredData = React.useMemo(() => {
    const data = fieldData?.data ?? [];

    return viewDifferencesOnly ? data.filter((row) => row.difference) : data;
  }, [viewDifferencesOnly, fieldData?.data]);

  // Auto select reinvestigation date when loading a new personnel diff, if there is a date to be updated
  useEffect(() => {
    const data = fieldData?.data ?? [];
    const reinvestigationRow = data.find((row) => row.id === 'reinvestigationDate');

    if (reinvestigationRow && reinvestigationRow.difference) {
      setSelectedFields((prev) => [...prev, 'reinvestigationDate']);
      setDisplayUpdateButton(true);
    } else {
      setSelectedFields((prev) => prev.filter((f) => f !== 'reinvestigationDate'));
      setDisplayUpdateButton(false);
    }
  }, [fieldData?.data])

  const allRowsNonSelectable = filteredData.length > 0 && filteredData.every((row) => !row.difference);

  const handleSwitchChange = useCallback((e) => {
    const newValue = e.target.checked;

    setViewDifferencesOnly(newValue);
    updateSessionInfo(sessionId, newValue);
  }, [sessionId, updateSessionInfo]);

  const handleUpdateRecord = async () => {
    await updateMutation.mutateAsync({ sessionId, selectedPersonnel, selectedFields });
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedFields(newSelection);
    setDisplayUpdateButton(newSelection.length > 0);
  };

  const handlePersonnelSelection = (e) => {
    setSelectedPersonnel(e);
    setMappedId(e?.id ?? '');
  };

  const columns = [
    {
      field: 'prettyName',
      headerName: 'Field',
      flex: 1,
      renderCell: (params) => (
        <>
          <Typography sx={{ fontWeight: params.row.difference ? 'bold' : 'inherit', color: params.row.difference ? 'warning.main' : 'inherit' }}>
            {params.value}
          </Typography>
          {params.id === 'pvqDate' && (
            <Tooltip color='warning' title='Reinvestigation date should also be updated if the PVQ date changes'>
              <InfoIcon fontSize='1rem' sx={{ marginLeft: 1 }} />
            </Tooltip>
          )}
        </>
      )
    },
    {
      field: 'securityControlValue',
      headerName: 'Security Control Value',
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: params.row.difference ? 'bold' : 'inherit' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'documentValue',
      headerName: 'Document Value',
      flex: 1,
      renderCell: (params) => (
        <>
          {params.id === 'reinvestigationDate' && (
            <Tooltip title='Reinvestigation date is derived from the PVQ date plus the configured reinvestigation period for the indoctrination level'>
              <Chip label='Calculated' size='small' sx={{ marginRight: 1 }} />
            </Tooltip>
          )}
          <Typography sx={{ fontWeight: params.row.difference ? 'bold' : 'inherit' }}>
            {params.value}
          </Typography>
        </>
      )
    }
  ];

  return (
    <Box sx={{ paddingBottom: 2 }}>
      <FormControl fullWidth variant='outlined'>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Autocomplete
            options={options ?? []}
            getOptionLabel={(option) => option.label || ''}
            value={selectedPersonnel}
            onChange={(_, newValue) => handlePersonnelSelection(newValue)}
            renderInput={(params) => (
              <TextField {...params} label='Personnel to Update' variant='outlined' />
            )}
            sx={{ width: 300, marginRight: 2 }}
            clearOnEscape
          />
          {selectedPersonnel && (
            <FormControlLabel
              control={(
                <Switch
                  checked={viewDifferencesOnly}
                  onChange={handleSwitchChange}
                />
              )}
              label='View Only differences'
            />
          )}
          {selectedPersonnel && allRowsNonSelectable && (
            <Alert severity='info'>
              Personnel record matches uploaded report
            </Alert>
          )}
        </Box>
      </FormControl>
      <DataGridPro
        rows={filteredData}
        columns={columns}
        checkboxSelection={!allRowsNonSelectable}
        disableSelectionOnClick
        isRowSelectable={(params) => params.row.difference}
        getRowId={(row) => row.id}
        autoHeight
        sx={{ marginTop: 2, display: selectedPersonnel ? 'block' : 'none' }}
        onSelectionModelChange={handleSelectionChange}
        selectionModel={selectedFields}
      />
      <FormFooter>
        {displayUpdateButton && (
          <Button onClick={handleUpdateRecord} color='primary' variant='contained' sx={{ minWidth: '100px' }}>
            Update
          </Button>
        )}
      </FormFooter>
    </Box>
  )
};

export default UpdateRecords;
